import { call, put, select, takeEvery } from 'redux-saga/effects'
import { handleApiError } from '../../../components/apiErrorHandler/store/apiErrorHandler.actions'
import { getAuthenticationJwtSelector } from '../../login/selectors/getAuthenticationJwt.selector'
import { EKGImportConstants } from '../store/ekgImport.constants'
import { EKGImportService } from '../services/ekgImport.service'
import {
  IEKGImportState,
  IEKGReport,
  IEKGReportImage,
  IFetchReportImageRequest,
  IFetchReportImageResponse,
} from '../store/ekgImport.types'
import { getEKGImportSelector } from '../selectors'
import { updateReportsList } from '../store/ekgImport.actions'
import { IMAGE_EXTENSIONS } from '../store/ekgImport.reducers'
import { isOffline } from '../../../utilities'

export function* getReportImage() {
  const authenticationJwt: string = yield select(getAuthenticationJwtSelector)
  const { reportsList, loadingReportId }: IEKGImportState = yield select(getEKGImportSelector)

  const idToUpdate = loadingReportId
  if (!idToUpdate) return

  const reportToUpdateIndex = (reportsList as Array<IEKGReport>).findIndex((report) => report.id === idToUpdate)
  if (reportToUpdateIndex === -1) return

  const reportToUpdate = reportsList[reportToUpdateIndex]
  if (reportToUpdate?.image || reportToUpdate?.image?.isUnavailable) return

  // If report is not an image, do not continue
  const reportToUpdateFileExtension = reportToUpdate.fileName?.split('.').pop()?.toLowerCase() || ''
  if (!IMAGE_EXTENSIONS.includes(reportToUpdateFileExtension)) {
    const nonImageReport: IEKGReportImage = {
      isLoading: false,
      isUnavailable: true,
      imageString: '',
      fileName: reportToUpdate.description,
    }

    reportToUpdate.image = nonImageReport
    reportsList[reportToUpdateIndex] = reportToUpdate

    yield put(updateReportsList(reportsList))
    return
  }

  const loadingReportImage: IEKGReportImage = {
    isLoading: true,
    isUnavailable: false,
    imageString: null,
    fileName: null,
  }
  reportToUpdate.image = loadingReportImage
  reportsList[reportToUpdateIndex] = reportToUpdate

  yield put(updateReportsList(reportsList))

  if (isOffline()) {
    const offlineReportImage: IEKGReportImage = {
      isLoading: false,
      isUnavailable: true,
      imageString: '',
      fileName: reportToUpdate.description,
    }

    reportToUpdate.image = offlineReportImage
    reportsList[reportToUpdateIndex] = reportToUpdate

    yield put(updateReportsList(reportsList))
    return
  }

  try {
    const ekgImportService = new EKGImportService(authenticationJwt as string)
    const fetchReportImageRequest: IFetchReportImageRequest = {
      reportId: idToUpdate,
    }

    const response: IFetchReportImageResponse = yield call(
      [ekgImportService, ekgImportService.fetchReportImage],
      fetchReportImageRequest
    )

    if (response.status === 200) {
      const imageObject = response.data.reportImage

      const loadedReportImage: IEKGReportImage = {
        isLoading: false,
        isUnavailable: !imageObject || imageObject.blobData === null,
        imageString: imageObject?.blobData || null,
        fileName: imageObject?.fileName || null,
      }

      reportToUpdate.image = loadedReportImage
      reportsList[reportToUpdateIndex] = reportToUpdate
      yield put(updateReportsList(reportsList))
    } else {
      response.failure = true
      if (response.messages?.length) {
        const index = response.messages.findIndex((x) => x.status === response.status)
        response.responseMessage = response.messages[index].message
      } else {
        response.responseMessage =
          'There was an error processing your request. Please contact customer support for further assistance.'
      }
      yield put(handleApiError(response))
    }
  } catch (e: any) {
    const cause = e.message ? e.message : 'unknown'
    const errorResponse = {
      status: null,
      messages: [
        {
          status: null,
          display: [
            { id: 3, name: 'Alert' },
            { id: 1, name: 'Log' },
          ],
          message: `An error occurred during EKG import. Cause: ${cause}`,
        },
      ],
    }
    yield put(handleApiError(errorResponse))
  }
}

export function* getReportImageSaga() {
  yield takeEvery([EKGImportConstants.GET_REPORT_IMAGE], getReportImage)
}
